import { ContractFiltersDto } from '../../services/contracts/ContractService';

const SECOND_MS = 1000;

/** Cache do modal no browser; mutações invalidam na hora. API usa TTL próprio (~20s). */
export const CONTRACT_MODAL_CACHE_STALE_TIME_MS = 3 * 60 * SECOND_MS;

type ContractsPrimaryRole = 'seller' | 'teamleader' | 'manager' | 'admin' | 'bko' | 'default';

const ROLE_PRIORITY: ContractsPrimaryRole[] = ['bko', 'admin', 'teamleader', 'manager', 'seller'];

function normalizeRole(role: string): ContractsPrimaryRole {
  const normalized = role.trim().toLowerCase();
  if (normalized === 'seller') return 'seller';
  if (normalized === 'teamleader') return 'teamleader';
  if (normalized === 'manager') return 'manager';
  if (normalized === 'admin') return 'admin';
  if (normalized === 'bko') return 'bko';
  return 'default';
}

export function resolveContractsPrimaryRole(roles: string[] | null | undefined): ContractsPrimaryRole {
  if (!roles?.length) return 'default';
  const normalizedRoles = roles.map(normalizeRole);
  for (const role of ROLE_PRIORITY) {
    if (normalizedRoles.includes(role)) return role;
  }
  return 'default';
}

function serializeFilterValue(value: unknown): unknown {
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return [...value].sort();
  return value;
}

export function normalizeFiltersForContractsQueryKey(filters: ContractFiltersDto): Record<string, unknown> {
  const normalizedEntries = Object.entries(filters)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => [key, serializeFilterValue(value)] as const)
    .sort(([a], [b]) => a.localeCompare(b));

  return Object.fromEntries(normalizedEntries);
}

/**
 * A lista de contratos não pode ficar em cache no browser.
 * Depois de criar um contrato, `fetchQuery` com staleTime de vários minutos
 * devolvia a página antiga e o contrato novo só aparecia quando esse prazo acabava.
 * O total (Count) é cacheado na API; as linhas vêm do banco em cada GET.
 */
export function resolveContractsListStaleTimeMs(
  _role: ContractsPrimaryRole,
  _pageNumber: number
): number {
  return 0;
}

export const contractsQueryKeys = {
  all: () => ['contracts'] as const,
  lists: () => [...contractsQueryKeys.all(), 'list'] as const,
  listForUser: (userId: string, role: ContractsPrimaryRole) =>
    [...contractsQueryKeys.lists(), userId || 'anonymous', role] as const,
  list: (userId: string, role: ContractsPrimaryRole, filters: ContractFiltersDto) =>
    [...contractsQueryKeys.listForUser(userId, role), normalizeFiltersForContractsQueryKey(filters)] as const,
  lineDetailsRoot: () => [...contractsQueryKeys.all(), 'line-details-modal'] as const,
  lineDetailsByContract: (contractId: string) =>
    [...contractsQueryKeys.lineDetailsRoot(), 'contract', contractId] as const,
  lineDetailsByGroup: (groupId: string) =>
    [...contractsQueryKeys.lineDetailsRoot(), 'group', groupId] as const,
};
